import React from 'react'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import { makeStyles } from '@material-ui/core/styles'
import { LinearProgress } from '@material-ui/core';
import {
  MapContainer,
  TileLayer,
  useMapEvents,
  Pane,
} from 'react-leaflet'
import L from 'leaflet'
import { find, flatMap } from 'lodash'

import Parcels from '@graniot/common/components/map/Parcels'
import Legend from './Legend'
import ParcelSelector from '@graniot/common/components/map/ParcelSelector'
import PublicParcelSelector from './PublicParcelSelector'
import DeforestationButton from '@graniot/common/components/map/DeforestationButton'
import PublicZoomControls from './tools/PublicZoomControls'

import FullScreenInfoDialog from './info-ui/FullScreenInfoDialog'
import MapControl from './MapControl'
import { useLocation } from '@graniot/common/context/LocationContext'
import WMSLayer from '@graniot/common/components/map/WMSLayer'
import GraniotWMSLayer from './GraniotWMSLayer'
import TreeLayerLegend from './TreeLayerLegend'
import DeforestationLayerLegend from './DeforestationLayerLegend'
import ButtonOpenDrawer from './ButtonOpenDrawer'

// Components not needed in public mode - removed
// CustomLayersDisplay, VectorTileLayer, LayerDataTable
import MapLayersManager from '@graniot/common/components/map/MapLayersManager'

// Center map on Granada
const position = [37.741934, -3.188515]

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
}))

function ListenZoomLevel({ setZoomLevel }) {
  const mapEvents = useMapEvents({
    zoomend: () => {
      setZoomLevel(mapEvents.getZoom())
    },
  })
  return null
}

function Map({
  setMap,
  map,
  farms,
  companyId,
  isLoadingFarms,
  openDrawer,
  setOpenDrawer,
  dispatchFarms,
  numberOfParcels,
  width,
  farmsHectares,
  drawerWidth,
  plantsCounter,
  isPublicMode = true,
  layers = [],
}) {
  const classes = useStyles()
  const [zoomLevel, setZoomLevel] = React.useState(9) // initial zoom level
  const [payload, setPayload] = React.useState({ open: false, payload: null })
  const [openEditParcel, setOpenEditParcel] = React.useState(false)
  const [focusParcel, setFocusParcel] = React.useState(null)
  // States not needed in public mode
  const openAddParcel = { open: false, mode: null }
  const setOpenAddParcel = () => {}
  const dispatchAddedParcels = () => {}
  const addedParcels = []
  const openDrawerCustomLayer = false
  const setOpenDrawerCustomLayer = () => {}
  const openMeasureTool = false // Set to false since measurement tool is removed
  const showIntroParcel = false
  const [showLastIntro, setShowLastIntro] = React.useState(false)
  const [data, setData] = React.useState(null)
  const [isDemLegendVisible, setIsDemLegendVisible] = React.useState(false)
  const [hasVisibleCustomLayers, setHasVisibleCustomLayers] = React.useState(false)

  // const { layers, selectedIndexLayer } = useLayers()
  // layers is now passed as a prop from PublicMapApp
  const selectedIndexLayer = layers[0]
  const wms_layer = layers.find(
    (layer) =>
      layer.name !== 'Parcels' &&
      !layer.is_sentinel &&
      layer.name.endsWith('WMS') &&
      layer.visible
  )

  const treeLayer = layers.find((layer) => layer.name == 'Tree')
  const deforestationLayer = layers.find((layer) => layer.name == 'Parcels')
  // const { selectedResolution } = useResolution()
  const selectedResolution = null // Will be overridden by effectiveResolution
  // No auth needed in public mode - create mock objects
  const user = { profile: { show_intro: false, embedded_conf: {} } }
  const isClientFreeTrialExpired = () => false
  const { location } = useLocation()

  // In public mode, force deforestation resolution - memoize to prevent infinite loops
  const effectiveResolution = React.useMemo(() =>
    isPublicMode
      ? { id: 1, resolution: 'DEFORESTATION' }
      : selectedResolution
  , [isPublicMode, selectedResolution])

  const selectedFarm = farms.find(farm => farm.checked)

  const hasDeforestationAuditData = React.useMemo(() => {
    if (!selectedFarm || !selectedFarm.farm_parcels) return false;

    // Get parcels from the GeoJSON features
    const parcels = selectedFarm.farm_parcels.features || [];
    if (parcels.length === 0) return false;

    // Check if any parcels have deforestation audit data
    // In public mode, we check for deforestation_v2 which comes from the public API
    return parcels.some(feature => {
      const parcel = feature.properties;
      return parcel && (parcel.deforestation_audit || parcel.deforestation_v2);
    });
  }, [selectedFarm])


  React.useEffect(() => {
    if (farms && effectiveResolution) {
      const features = flatMap(
        farms.filter((farm) => farm.checked),
        (farm) =>
          isPublicMode
            ? farm.farm_parcels.features // In public mode, show all features
            : farm.farm_parcels.features.filter((f) =>
                find(f.properties.parcelresolution_set, {
                  resolution: effectiveResolution.id,
                })
              )
      )
      setData({
        type: 'FeatureCollection',
        features: features,
      })
    }
  }, [farms, effectiveResolution, isPublicMode])

  const centerMap = (bbox) => {
    if (!map || !bbox) return
    var southWest = new L.LatLng(bbox[1], bbox[0]),
      northEast = new L.LatLng(bbox[3], bbox[2]),
      bounds = new L.LatLngBounds(southWest, northEast)
    map.fitBounds(bounds)
  }

  React.useEffect(() => {
    if (focusParcel) {
      const { bbox } = focusParcel
      centerMap(bbox)
    }
  }, [focusParcel])

  React.useEffect(() => {
    if (location && map) {
      const { bbox, latLng } = location
      if (bbox) centerMap(bbox)
      else if (latLng) map.flyTo(latLng, 15)
    }
  }, [location, map])

  React.useEffect(() => {
    if (openAddParcel.open) setFocusParcel(null)
  }, [openAddParcel.open])


  const handleOpenDialog = (payload) => {
    // Disable click functionality in public mode
    if (isPublicMode) {
      return
    }

    if (user.profile?.embedded_conf?.only_edit_parcel) {
      setPayload({ open: true, ...payload })
      setOpenEditParcel(true)
    } else {
      setPayload({ open: true, ...payload })
    }
  }
  return (
    <React.Fragment>
      <MapContainer
        center={companyId === 400 ? [3.1390, 101.6869] : position}
        minZoom={2}
        maxZoom={20}
        zoom={zoomLevel}
        style={{ width: '100%', height: '100vh' }}
        onzoomend={() => console.log(map?.getZoom())}
        zoomControl={false}
        whenCreated={setMap}
        className="main-map"
      >
        {/* CustomLayersDisplay removed - not needed in public mode */}
        <ListenZoomLevel setZoomLevel={setZoomLevel} />

        {!isWidthUp('sm', width) && (
          <TileLayer
            attribution="Maps Hybrid"
            url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`}
          />
        )}
        <MapLayersManager
          companyId={companyId}
          map={map}
          width={width}
          openDrawer={openDrawer}
          drawerWidth={drawerWidth}
          openAddParcel={openAddParcel}
          isPublic
          noAppBar
          onDemLegendVisibilityChange={setIsDemLegendVisible}
        />


        {/* SigPACLayer removed - not needed in public mode */}

        {isLoadingFarms && <LinearProgress style={{ zIndex: 1000 }} />}

        {data && data.features.length > 0 && (
          <React.Fragment>
            {/* SentinelLayer removed - not needed in public mode */}

            {effectiveResolution.resolution === 'DEFORESTATION' && (
              <DeforestationLayerLegend
                layer={selectedIndexLayer}
                openDrawer={openDrawer}
                drawerWidth={drawerWidth}
                isDemLegendVisible={isDemLegendVisible}
                width={width}
              />
            )}

            {!hasVisibleCustomLayers && (openAddParcel.open || openMeasureTool ? (
              <Parcels
                key={'drawMode' + farms.find(farm => farm?.checked)?.id}
                parcels={data}
                layersState={layers}
                handleOpenDialog={() => null}
                parcelColor="#80880"
                drawMode={true}
                detectOverlapping={false}
                hasDeforestationAuditData={hasDeforestationAuditData}
              />
            ) : (
              <Parcels
                key={'viewMode' + farms.find(farm => farm?.checked)?.id}
                parcels={data}
                layersState={layers}
                handleOpenDialog={handleOpenDialog}
                parcelColor="#ffea00"
                embedded={true}
                drawMode={false}
                detectOverlapping={true}
                hasDeforestationAuditData={hasDeforestationAuditData}
              />
            ))}

            {/* ParcelIntro removed - not needed in public mode */}

            {/* CloudsLayer removed - not needed in public mode */}
          </React.Fragment>
        )}

        {/* DrawParcels removed - not needed in public mode */}

        {/* MunicipalityParcels removed - not needed in public mode */}

        {/* MeasureTool removed from public mode */}
      </MapContainer>

      {layers
        .filter((l) => (l.is_sentinel || l.name.endsWith('WMS')) && l.visible)
        .map((layer, index) => (
          <Legend key={index} layersState={layers} layer={layer} />
        ))}

      <MapControl
        position="top"
        drawerWidth={drawerWidth}
        openDrawer={openDrawer}
      >
        {/* Drawing tools removed - not needed in public mode */}

        {numberOfParcels > 0 && (
          <>
             {isPublicMode ? (
              <PublicParcelSelector
                parcels={data?.features}
                focusParcel={focusParcel}
                setFocusParcel={setFocusParcel}
                centerMap={centerMap}
              />
            ) : (
              <ParcelSelector
                parcels={data?.features}
                focusParcel={focusParcel}
                setFocusParcel={setFocusParcel}
                centerMap={centerMap}
              />
            )}

            {hasDeforestationAuditData && isPublicMode && (
              <DeforestationButton
                selectedFarm={data}
                isPublicMode={true}
              />
            )}
          </>
        )}

      </MapControl>

     {isPublicMode && (
        <PublicZoomControls mapInfo={map} />
      )}

    </React.Fragment>
  )
}

export default withWidth()(Map)
